'use client'

import React, { useEffect, useMemo, useRef } from 'react'
import { twMerge } from 'tailwind-merge'
import type { FieldError as HookFieldError } from 'react-hook-form'
import FieldError from '@/components/livecontact/FieldError'

export type OTPInputProps = {
  className?: string
  classInput?: string
  label?: string
  note?: string
  length?: number
  value: string
  onChange: (value: string) => void
  onComplete?: (value: string) => void
  autoFocus?: boolean
  disabled?: boolean
  error?: HookFieldError | string
  mask?: boolean
}

const digitOnly = (s: string) => (s.match(/\d/g) || []).join('')

const OTPField: React.FC<OTPInputProps> = ({
  className,
  classInput,
  label,
  note,
  length = 6,
  value,
  onChange,
  onComplete,
  autoFocus,
  disabled,
  error,
  mask,
}) => {
  const inputsRef = useRef<Array<HTMLInputElement | null>>([])
  const errMessage = typeof error === 'string' ? error : error?.message

  const cells = useMemo(() => {
    const v = (value || '').slice(0, length)
    return Array.from({ length }, (_, i) => v[i] ?? '')
  }, [value, length])

  // Focus the first empty cell when autoFocus enabled
  useEffect(() => {
    if (!autoFocus || disabled) return
    const idx = cells.findIndex((c) => c === '')
    const target = inputsRef.current[idx >= 0 ? idx : length - 1]
    target?.focus()
    target?.select?.()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [autoFocus, disabled])

  // Helper to update value string based on cell edits
  const setCell = (index: number, char: string) => {
    const next = cells.slice()
    next[index] = char
    const nextValue = next.join('')
    onChange(nextValue)
    if (onComplete && nextValue.length === length && next.every((c) => c !== '')) {
      onComplete(nextValue)
    }
  }

  const handleChange = (index: number) => (e: React.ChangeEvent<HTMLInputElement>) => {
    if (disabled) return
    let val = e.target.value

    // If user pasted multiple chars into a single cell
    if (val.length > 1) {
      const pasted = digitOnly(val).slice(0, length - index)
      if (!pasted) return
      const next = cells.slice()
      for (let i = 0; i < pasted.length; i++) next[index + i] = pasted[i]
      const nextValue = next.join('')
      onChange(nextValue)
      // Move focus to the cell after the last pasted char
      const focusIdx = Math.min(index + pasted.length, length - 1)
      inputsRef.current[focusIdx]?.focus()
      inputsRef.current[focusIdx]?.select?.()
      if (onComplete && nextValue.length === length && next.every((c) => c !== '')) {
        onComplete(nextValue)
      }
      return
    }

    // Single char typed: accept only digits
    val = digitOnly(val).slice(0, 1)
    if (!val) return

    setCell(index, val)

    // Move to next cell
    const nextInput = inputsRef.current[index + 1]
    nextInput?.focus()
    nextInput?.select?.()
  }

  const handleKeyDown = (index: number) => (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (disabled) return

    const current = inputsRef.current[index]

    switch (e.key) {
      case 'Backspace': {
        if (cells[index]) {
          // Clear the current cell
          e.preventDefault()
          setCell(index, '')
          current?.setSelectionRange?.(0, 0)
        } else if (index > 0) {
          // Move to previous and clear it
          e.preventDefault()
          const prev = inputsRef.current[index - 1]
          prev?.focus()
          prev?.select?.()
          setCell(index - 1, '')
        }
        break
      }
      case 'Delete': {
        e.preventDefault()
        setCell(index, '')
        break
      }
      case 'ArrowLeft':
      case 'Left': {
        e.preventDefault()
        if (index > 0) {
          inputsRef.current[index - 1]?.focus()
          inputsRef.current[index - 1]?.select?.()
        }
        break
      }
      case 'ArrowRight':
      case 'Right': {
        e.preventDefault()
        if (index < length - 1) {
          inputsRef.current[index + 1]?.focus()
          inputsRef.current[index + 1]?.select?.()
        }
        break
      }
      case 'Home': {
        e.preventDefault()
        inputsRef.current[0]?.focus()
        inputsRef.current[0]?.select?.()
        break
      }
      case 'End': {
        e.preventDefault()
        inputsRef.current[length - 1]?.focus()
        inputsRef.current[length - 1]?.select?.()
        break
      }
      case 'Enter': {
        if (onComplete && value.length === length && cells.every((c) => c !== '')) {
          onComplete(value)
        }
        break
      }
      default:
        break
    }
  }

  const handlePaste = (index: number) => (e: React.ClipboardEvent<HTMLInputElement>) => {
    if (disabled) return
    const text = e.clipboardData.getData('text')
    const pasted = digitOnly(text)
    if (!pasted) return
    e.preventDefault()
    const next = cells.slice()
    let i = 0
    for (; i < pasted.length && index + i < length; i++) {
      next[index + i] = pasted[i]
    }
    const nextValue = next.join('')
    onChange(nextValue)
    const focusIdx = Math.min(index + i, length - 1)
    inputsRef.current[focusIdx]?.focus()
    inputsRef.current[focusIdx]?.select?.()
    if (onComplete && nextValue.length === length && next.every((c) => c !== '')) {
      onComplete(nextValue)
    }
  }

  const baseInputClass = `
    size-11 rounded-[10px] bg-surface text-center text-[18px] font-medium tabular-nums text-ink shadow-input outline-none
    transition-[box-shadow,transform] duration-[160ms] ease-out-strong placeholder:text-ink-4
    focus:shadow-[0_4px_4px_-3px_rgba(0,0,0,0.1),0_0_0_1px_rgb(var(--lc-brand)),0_0_0_4px_rgb(var(--lc-brand)/0.14)]
    ${disabled ? 'cursor-not-allowed opacity-60' : ''}
    ${errMessage ? 'shadow-[0_4px_4px_-3px_rgba(0,0,0,0.1),0_0_0_1px_#D33]' : ''}
  `

  return (
    <div className={twMerge('', className)}>
      {label && (
        <div className="mb-1.5 text-[12px] font-medium leading-[1.4] tracking-[-0.2px] text-ink-3">
          {label}
        </div>
      )}

      <div
        className={twMerge(
          'flex items-center justify-center gap-2',
          disabled && 'pointer-events-none'
        )}
        role="group"
        aria-label={label || 'One-time password'}
      >
        {cells.map((c, i) => (
          <input
            key={i}
            ref={(el) => {
              inputsRef.current[i] = el
            }}
            type={mask ? 'password' : 'text'}
            inputMode="numeric"
            autoComplete={i === 0 ? 'one-time-code' : 'off'}
            maxLength={1}
            className={twMerge(baseInputClass, classInput)}
            value={c}
            onChange={handleChange(i)}
            onKeyDown={handleKeyDown(i)}
            onPaste={handlePaste(i)}
            onFocus={(e) => e.currentTarget.select()}
            aria-label={`Digit ${i + 1} of ${length}`}
            aria-invalid={!!errMessage}
            disabled={disabled}
          />
        ))}
      </div>

      {note && (
        <p className="mt-1.5 text-[12px] leading-[1.4] tracking-[-0.2px] text-ink-4">{note}</p>
      )}

      <FieldError message={errMessage} />
    </div>
  )
}

export default OTPField
